import type { ReactNode } from 'react'
import { Fragment } from 'react'

import Typography from '@segment/matcha/components/Typography'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

interface Section {
  header: string
  rows: Row[]
}

interface Row {
  header: string
  tooltip?: string
  cells: ReactNode[]
  subRows?: SubRow[]
}

type SubRow = Omit<Row, `subRows`>

const sections = (params: string): Section[] => {
  const pageConfig = usePageConfig()
  return [
    {
      header: `Collect and send data anywhere`,
      rows: [
        {
          header: `Sources`,
          tooltip: `Use a single API to capture and send customer data exactly where you need it`,
          cells: [`2 Sources`, `Unlimited`, `Unlimited`],
          subRows: [
            {
              header: `Web`,
              tooltip: `Collect customer data from your website with the Segment javascript library`,
              cells: [true, true, true],
            },
            {
              header: `Mobile`,
              tooltip: `Collect customer data from your mobile apps with SDKs for iOS, Android, and more`,
              cells: [true, true, true],
            },
            {
              header: `Server`,
              tooltip: `Send customer data directly from your servers`,
              cells: [true, true, true],
            },
            {
              header: `Cloud Apps`,
              tooltip: `Connect platforms like Salesforce, Stripe, Facebook Ads, and more`,
              cells: [true, true, true],
            },
          ],
        },
        {
          header: `Destinations`,
          tooltip: `Send your data to ${process.env.INTEGRATION_COUNT}+ tools for product analytics, advertising, email, and more`,
          cells: [`Unlimited`, `Unlimited`, `Unlimited`],
        },
        {
          header: `Functions`,
          tooltip: `Create your own sources and destinations directly within your workspace. This is an optional feature subject to additional terms.`,
          cells: [`50 Hours`, `50 Hours`, `Custom`],
        },
        {
          header: `Warehouses`,
          tooltip: `Transform and load your data into Redshift, BigQuery, Postgres, Snowflake, or IBM DB2`,
          cells: [`1 Warehouse`, `1 Warehouse`, `Unlimited`],
          subRows: [
            {
              header: `Sync frequency`,
              tooltip: `Choose how often and when you load data into your warehouse`,
              cells: [`1 per day`, `2 per day`, `Custom Schedule`],
            },
            {
              header: `Selective sync`,
              tooltip: `Choose which events and properties sync to your warehouse`,
              cells: [false, false, true],
            },
          ],
        },
        {
          header: `Reverse ETL`,
          tooltip: `Activate data from the warehouse into ${process.env.INTEGRATION_COUNT}+ tools for product analytics, advertising, email, and more`,
          cells: [true, true, true],
        },
        {
          header: `Developer Tools`,
          tooltip: `Set up, test, and swap new tools with a common UI and data observability`,
          cells: [true, true, true],
          subRows: [
            {
              header: `Central view of data schema`,
              tooltip: `See a list of all events, traits, and more you've sent to Segment`,
              cells: [true, true, true],
            },
            {
              header: `Live data view and debugger`,
              tooltip: `View live data passing through Segment to test your tracking code`,
              cells: [true, true, true],
            },
          ],
        },
        {
          header: `Public API`,
          tooltip: `Programmatically manage Segment workspaces, sources, destinations, and more`,
          cells: [false, true, true],
        },
      ],
    },
    {
      header: `Control your data quality`,
      rows: [
        {
          header: `Data privacy and controls`,
          tooltip: `Take a proactive approach to privacy and easily control where your data flows `,
          cells: [true, true, true],
          subRows: [
            {
              header: `User deletion`,
              tooltip: `Manage user deletion across Segment and supported destinations`,
              cells: [true, true, true],
            },
            {
              header: `User suppression`,
              tooltip: `Block data collection for specific users with one-click suppression`,
              cells: [true, true, true],
            },
            {
              header: `Customer data discovery and inventory`,
              tooltip: `Automatically detect and classify personal information in real time`,
              cells: [true, true, true],
            },
            {
              header: `Destination filters`,
              tooltip: `Activate the data flowing into each destination`,
              cells: [false, false, true],
            },
            {
              header: `Replay historical data`,
              tooltip: `Load all of your historical data into new tools`,
              cells: [false, false, true],
            },
          ],
        },
        {
          header: `Advanced data governance: Protocols`,
          tooltip: `Automatically enforce data quality`,
          cells: [false, false, `+ Add-on`],
          subRows: [
            {
              header: `Integrated tracking plan`,
              tooltip: `Create and manage a data tracking plan in your workspace`,
              cells: [false, false, `+ Add-on`],
            },
            {
              header: `Data validation`,
              tooltip: `QA your implementation with automated data validation and violation reporting`,
              cells: [false, false, `+ Add-on`],
            },
            {
              header: `Enforcement`,
              tooltip: `Automatically prevent unplanned or invalid data from being sent to Segment`,
              cells: [false, false, `+ Add-on`],
            },
            {
              header: `Transformations`,
              tooltip: `Transform events without code`,
              cells: [false, false, `+ Add-on`],
            },
          ],
        },
      ],
    },
    {
      header: `Complete view of your customer`,
      rows: [
        {
          header: `Unified customer data`,
          tooltip: `Send data to a data warehouse to tie disparate events and interactions into unified profiles`,
          cells: [true, true, true],
        },
        {
          header: `Identity Resolution`,
          tooltip: `Unify user history across devices and channels into one comprehensive profile`,
          cells: [false, false, `+ Add-on`],
        },
        {
          header: `Profile API Access`,
          tooltip: `Programmatic access to Personas for in-app and onsite personalization`,
          cells: [false, false, `+ Add-on`],
        },
        {
          header: `Profiles Sync`,
          tooltip: `Sync identity resolved customer profiles to the warehouse`,
          cells: [false, false, `+ Add-on`],
        },
      ],
    },
    {
      header: `Build personalized customer experiences`,
      rows: [
        {
          header: `Trait and audience building`,
          tooltip: `Create real-time audiences and profiles`,
          cells: [false, false, `+ Add-on`],
        },
        {
          header: `Journeys`,
          tooltip: `Orchestrate and experiment with personalized journeys across multiple channels based on real-time customer behavior`,
          cells: [false, false, `+ Add-on`],
        },
        {
          header: `Channels (native SMS/email currently not supported in EMEA)`,
          tooltip: `Deliver personalized experiences where, when, and how your customers want with our native email and SMS channels, custom channels and ${process.env.INTEGRATION_COUNT}+ out-of-the-box integrations`,
          cells: [false, false, `+ Add-on`],
        },
      ],
    },

    {
      header: `Usage and billing`,
      rows: [
        {
          header: `Monthly Tracked Users`,
          tooltip: `The number of anonymous and logged-in visitors that you track with Segment`,
          cells: [`Up to 1,000`, `Base includes 10,000`, `Custom volume`],
          subRows: [
            {
              header: `Throughput Limit`,
              tooltip: `Your customer data comes in the form of API Calls and Objects. Free and Team plans include up to 250 API Calls and Objects per MTU. The vast majority of Segment customers use well under these limits. Business plans are eligible for custom limits.`,
              cells: [`Standard`, `Standard`, `Custom`],
            },
          ],
        },
        {
          header: `Price`,
          cells: [`Free`, `Starting at $120/month`, `Custom`],
          subRows: [
            { header: `Additional 1,000 monthly tracked users (10K - 25K)`, cells: [false, `+ $12`, false] },
            { header: `Additional 1,000 monthly tracked users (25K - 100K)`, cells: [false, `+ $11`, false] },
            { header: `Additional 1,000 monthly tracked users (100K+)`, cells: [false, `+ $10`, false] },
          ],
        },
        {
          header: `Seats`,
          tooltip: `The number of teammates that can access your Segment workspace`,
          cells: [`10`, `10`, `Unlimited`],
        },
        {
          header: `Payment Method`,
          cells: [false, `Credit Card`, `Invoice`],
        },
        {
          header: `Contract Length`,
          cells: [
            false,
            <Fragment key="annual-or-monthly">
              Annual or Monthly
              <br />(
              <Typography variant="pMedium" tag="span">
                <a href={getAbsoluteUrl(`/signup/?plan=team-yearly${params ? `&${params}` : ``}`, pageConfig)}>
                  save up to 20% annually
                </a>
              </Typography>
              )
            </Fragment>,
            `Unlimited`,
          ],
        },
      ],
    },
    {
      header: `Support`,
      rows: [
        {
          header: `Email/Web support`,
          cells: [true, true, true],
        },
        {
          header: `On-demand videos and documentation`,
          tooltip: `Get up-and-running quickly with developer documentation and on-demand training`,
          cells: [true, true, true],
        },
        {
          header: `Dedicated Support`,
          cells: [false, false, `Available`],
          subRows: [
            {
              header: `Customer Success Manager`,
              tooltip: `Customer Success Manager may be included based on your annual agreement. Please ask your Account Executive for details.`,
              cells: [false, false, `Available`],
            },
            {
              header: `Advisory Services or Professional Services`,
              tooltip: `Custom advisory services and professional services packages available. Please ask your Account Executive for details.`,
              cells: [false, false, `+ Add-on`],
            },
          ],
        },
        {
          header: `Uptime SLA`,
          tooltip: `Guaranteed API uptime`,
          cells: [true, true, true],
        },
      ],
    },
    {
      header: `Security`,
      rows: [
        {
          header: `Account Security`,
          tooltip: `Protect your workspace with personalized security settings`,
          cells: [true, true, true],
          subRows: [
            {
              header: `Multi-factor authentication`,
              tooltip: `Provide an additional layer of security beyond your username and password. When logging into Segment, users will also enter a code from their mobile phone.`,
              cells: [true, true, true],
            },
            {
              header: `Single Sign-on`,
              tooltip: `Centrally manage your policies for access with Single Sign-On (SSO)`,
              cells: [false, false, true],
            },
            {
              header: `Granular roles and user permissions`,
              tooltip: `Control access to your sources and workspaces with fine-grained permissions to manage how your teammates interact with your data`,
              cells: [false, false, true],
            },
          ],
        },
        {
          header: `Certifications`,
          tooltip: `To learn more about Segment's security and privacy program, and certifications and attestations, visit here`,
          cells: [true, true, true],
        },
        {
          header: `HIPAA-eligibility`,
          tooltip: `Store and manage PHI within your workspace`,
          cells: [false, false, `+ Add-on`],
        },
        {
          header: `Regional Segment (EU or US)`,
          tooltip: `Segment offers regional infrastructure in both Europe and the United States`,
          cells: [false, false, true],
        },
      ],
    },
  ]
}

export default sections
